import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Typography,
  message,
  Tooltip
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import {
  getTransactionData,
  bulkCheckFillStatus,
} from '../services/transactionService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    transaction_type: '',
    stock_name: '',
    date_from: '',
    date_to: '',
  });
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = await getTransactionData(filters);
      setTransactions(data.results || []);
    } catch (error) {
      message.error(`Failed to load transactions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCheckStatus = async () => {
    const pendingTransactions = transactions
      .filter(t => t.order_status === 'pending' || t.order_status === 'partially_filled')
      .map(t => t.id);

    if (pendingTransactions.length === 0) {
      message.info('No pending orders to check');
      return;
    }

    try {
      setLoading(true);
      const result = await bulkCheckFillStatus(pendingTransactions);

      if (result.success) {
        // Update transactions
        setTransactions(prev =>
          prev.map(t => {
            const statusResult = result.results[t.id];
            if (statusResult && !statusResult.error) {
              return {
                ...t,
                order_status: statusResult.order_status,
                filled_quantity: statusResult.filled_quantity,
                filled_price: statusResult.filled_price,
                fill_rate: statusResult.fill_rate,
              };
            }
            return t;
          })
        );

        message.success(`${pendingTransactions.length} orders checked`);
      }
    } catch (error) {
      message.error(`Bulk check failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: '접수',
      partially_filled: '부분체결',
      filled: '체결완료',
      cancelled: '취소',
      rejected: '거부',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: 'gold',
      partially_filled: 'orange',
      filled: 'green',
      cancelled: 'default',
      rejected: 'red',
    };
    return colorMap[status] || 'default';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('HH:mm');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        date_from: dates[0].format('YYYY-MM-DD'),
        date_to: dates[1].format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        date_from: '',
        date_to: '',
      }));
    }
  };

  const handleSearch = () => {
    fetchTransactions();
  };

  const handleReset = () => {
    setFilters({
      transaction_type: '',
      stock_name: '',
      date_from: '',
      date_to: '',
    });
    setDateRange(null);
    setTimeout(() => fetchTransactions(), 100);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 105,
      render: (text) => moment(text).format('YYYY-MM-DD'),
    },
    {
      title: 'Stock',
      dataIndex: 'stock_name',
      key: 'stock_name',
      width: 150,
      ellipsis: true,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || record.stock}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.stock_symbol}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Account',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 100,
      ellipsis: true,
      render: (text, record) => text || record.account,
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 70,
      render: (type) => (
        <Tag color={type === 'BUY' ? 'blue' : 'red'}>
          {type === 'BUY' ? '매수' : '매도'}
        </Tag>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 85,
      align: 'right',
      render: (quantity, record) => {
        if (record.filled_quantity > 0 && record.filled_quantity < quantity) {
          return (
            <Text>
              {formatNumber(record.filled_quantity)}/{formatNumber(quantity)}
            </Text>
          );
        }
        return formatNumber(quantity);
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 105,
      align: 'right',
      render: (price, record) => {
        if (record.filled_price && record.filled_price !== price) {
          return (
            <Tooltip title={`Order price: ₩${formatNumber(price)}`}>
              <Text>₩{formatNumber(record.filled_price)}</Text>
            </Tooltip>
          );
        }
        return `₩${formatNumber(price)}`;
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 115,
      align: 'right',
      render: (_, record) => {
        const amount = record.price * record.quantity;
        return <Text strong>₩{formatNumber(amount)}</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      key: 'order_status',
      width: 90,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Order Time',
      dataIndex: 'order_time',
      key: 'order_time',
      width: 70,
      render: (time) => {
        if (!time) return '-';
        return (
          <Text style={{ fontSize: '12px' }}>
            {formatDate(time)}
          </Text>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <ClockCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  <Title level={2} style={{ margin: 0 }}>
                    Transactions
                  </Title>
                </Space>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleBulkCheckStatus}
                    loading={loading}
                  >
                    Check Pending Orders
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchTransactions}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </Space>
              </div>

              {/* Filters */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Text strong>Type</Text>
                  <Select
                    placeholder="All types"
                    value={filters.transaction_type || undefined}
                    onChange={(value) => handleFilterChange('transaction_type', value)}
                    style={{ width: '100%', marginTop: '4px' }}
                    allowClear
                  >
                    <Option value="BUY">매수</Option>
                    <Option value="SELL">매도</Option>
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Text strong>Stock Name</Text>
                  <Input
                    placeholder="Search by stock name"
                    prefix={<SearchOutlined />}
                    value={filters.stock_name}
                    onChange={(e) => handleFilterChange('stock_name', e.target.value)}
                    style={{ marginTop: '4px' }}
                    allowClear
                  />
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <Text strong>Date Range</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    style={{ width: '100%', marginTop: '4px' }}
                  />
                </Col>

                <Col xs={24} sm={12} md={4}>
                  <div style={{ marginTop: '20px' }}>
                    <Space>
                      <Button type="primary" onClick={handleSearch}>
                        Search
                      </Button>
                      <Button onClick={handleReset}>Reset</Button>
                    </Space>
                  </div>
                </Col>
              </Row>

              {/* Table */}
              <Table
                columns={columns}
                dataSource={transactions}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} transactions`,
                }}
                tableLayout="fixed"
                size="small"
              />
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Transactions;
